import { STICKY_BANNER_EVENT_TYPES, STICKY_PAGE_TYPES } from "../stickyBannerConfig.types";
import {
  MATHS_OLYMPIAD_SCORE_SCROLL_MATRIX,
  REPORT_SCORE_SCROLL_MATRIX,
  REPORT_SUBJECT_FALLBACK_MESSAGE_IDS,
  STICKY_MESSAGE_REGISTRY
} from "./stickyBannerMessages";
import { resolveScoreBandId, SCORE_BAND_IDS } from "./stickyBannerScoreMatrix";
import {
  applySubjectPlaceholders,
  messagePartsNeedSubject,
  resolvePrioritySubject
} from "./subjectResolver";

export const REPORT_MILESTONES = {
  TOP: "top",
  MIDDLE: "middle",
  BOTTOM: "bottom"
};

/**
 * @param {string|undefined} eventType
 * @returns {typeof REPORT_SCORE_SCROLL_MATRIX}
 */
export function getReportScoreScrollMatrix(eventType) {
  if (eventType === STICKY_BANNER_EVENT_TYPES.MATHS_OLYMPIAD) {
    return MATHS_OLYMPIAD_SCORE_SCROLL_MATRIX;
  }
  return REPORT_SCORE_SCROLL_MATRIX;
}

/**
 * Resolves which score-matrix variant to use. Config wins for explicit olympiad;
 * otherwise Maths Olympiad is detected from the selected exam title (e.g. `/reports/:id`
 * still uses `reportsPageStickyBannerConfig` but must not lose olympiad copy).
 *
 * @param {{ stickyBannerEventType?: string } | null | undefined} config
 * @param {string|null|undefined} examTitle e.g. `examdetailes.title`
 * @returns {string} STICKY_BANNER_EVENT_TYPES value
 */
export function inferStickyBannerEventType(config, examTitle) {
  if (config?.stickyBannerEventType === STICKY_BANNER_EVENT_TYPES.MATHS_OLYMPIAD) {
    return STICKY_BANNER_EVENT_TYPES.MATHS_OLYMPIAD;
  }
  const compact = String(examTitle ?? "")
    .toLowerCase()
    .trim()
    .replace(/[\s\W]+/g, "");
  if (compact === "matholympiad" || compact === "mathsolympiad") {
    return STICKY_BANNER_EVENT_TYPES.MATHS_OLYMPIAD;
  }
  return config?.stickyBannerEventType ?? STICKY_BANNER_EVENT_TYPES.GENERIC_REPORT;
}

/**
 * Maps stable milestone phase from hook → scroll milestone key for matrix lookup.
 * Phase 1 = top of report, 2 = section-wise / middle, 3 = near bottom.
 */
export function mapReportPhaseToMilestone(phase) {
  if (phase >= 3) return REPORT_MILESTONES.BOTTOM;
  if (phase === 2) return REPORT_MILESTONES.MIDDLE;
  return REPORT_MILESTONES.TOP;
}

/**
 * @param {string} messageId
 * @param {string} [defaultCta]
 * @param {string} [source]
 * @returns {{ messageParts: Array<{kind:string,content:string}>; ctaText: string; matchedRuleId: string; source: string; scoreBandId?: string; milestone?: string } | null}
 */
export function getMessagePayloadById(messageId, defaultCta = "Book a Session", source = "registry") {
  if (!messageId) return null;
  const raw = STICKY_MESSAGE_REGISTRY[messageId];
  if (!raw?.messageParts?.length) return null;
  return {
    messageParts: raw.messageParts.map((p) => ({ ...p })),
    ctaText: raw.ctaText ?? defaultCta,
    matchedRuleId: messageId,
    source
  };
}

/**
 * REPORT page: resolve (score band × scroll milestone) → registry payload,
 * with optional `[Subject]` injection from `subjectSections` (generic matrix only).
 * @param {{
 *   scorePercent?: number|null,
 *   milestone: string,
 *   defaultCta?: string,
 *   subjectSections?: Array<{ sectionName?: string; marksObtained?: unknown; totalMarks?: unknown }>,
 *   eventType?: string,
 *   scoreBand?: string|null
 * }} input
 */
export function resolveReportScoreScrollMessage({
  scorePercent,
  milestone,
  defaultCta = "Book a Session",
  subjectSections,
  eventType = STICKY_BANNER_EVENT_TYPES.GENERIC_REPORT,
  scoreBand: scoreBandOverride
}) {
  const matrix = getReportScoreScrollMatrix(eventType);
  const bandId =
    typeof scoreBandOverride === "string" && matrix[scoreBandOverride]
      ? scoreBandOverride
      : resolveScoreBandId(scorePercent);
  const row = matrix[bandId] ?? matrix[SCORE_BAND_IDS.FALLBACK];
  let messageId = row?.[milestone] ?? row?.top ?? "report.fallbackBand.top";
  let payload = getMessagePayloadById(messageId, defaultCta, "report-matrix");
  if (!payload) return null;

  const subject = resolvePrioritySubject(subjectSections ?? []);
  const needsSubject = messagePartsNeedSubject(payload.messageParts);
  if (needsSubject && !subject) {
    const fbId = REPORT_SUBJECT_FALLBACK_MESSAGE_IDS[messageId];
    if (fbId) {
      messageId = fbId;
      const fbPayload = getMessagePayloadById(fbId, defaultCta, "report-matrix-fallback");
      if (fbPayload) payload = fbPayload;
    }
  } else if (subject && needsSubject) {
    payload = {
      ...payload,
      messageParts: applySubjectPlaceholders(payload.messageParts, subject.displayName)
    };
  }

  return {
    ...payload,
    matchedRuleId: messageId,
    scoreBandId: bandId,
    milestone
  };
}

/**
 * Centralized sticky copy resolution by page + event + score context.
 * Pure — safe to call from memoized selectors / hooks.
 *
 * @param {{
 *   pageType: string;
 *   eventType?: string;
 *   scoreBand?: string|null;
 *   milestone: string;
 *   scorePercent?: number|null;
 *   defaultCta?: string;
 *   subjectSections?: Array<{ sectionName?: string; marksObtained?: unknown; totalMarks?: unknown }>
 * }} params
 */
export function resolveStickyBannerMessage(params) {
  const { pageType, eventType, scoreBand, milestone, defaultCta, subjectSections, scorePercent } = params;

  if (pageType === STICKY_PAGE_TYPES.REPORT) {
    return resolveReportScoreScrollMessage({
      scorePercent,
      milestone,
      defaultCta,
      subjectSections,
      eventType: eventType ?? STICKY_BANNER_EVENT_TYPES.GENERIC_REPORT,
      scoreBand
    });
  }

  if (pageType === STICKY_PAGE_TYPES.EXAM) {
    const cta = defaultCta ?? "Book a Session";
    const payload = getMessagePayloadById("exam.results.guidance", cta, "exam-static");
    if (!payload) return null;
    return { ...payload, matchedRuleId: "exam.results.guidance" };
  }

  return null;
}

/**
 * Expand scroll/priority trigger: prefer `messageId`, else legacy `messageParts`.
 */
export function resolveTriggerPayload(trigger, config) {
  if (!trigger) return null;
  const defaultCta = config?.defaultCtaText ?? "Book a Session";
  if (trigger.messageId) {
    return getMessagePayloadById(trigger.messageId, defaultCta, "scroll-priority");
  }
  if (trigger.messageParts?.length) {
    return {
      messageParts: trigger.messageParts.map((p) => ({ ...p })),
      ctaText: trigger.ctaText ?? defaultCta,
      matchedRuleId: trigger.id,
      source: "scroll-priority-legacy"
    };
  }
  return null;
}

/**
 * Carousel by ordered message ids (exam page).
 */
export function resolveCarouselMessageIds(config, slideIndex) {
  const ids = config?.carouselMessageIds;
  if (!Array.isArray(ids) || !ids.length) return null;
  const i = ((slideIndex % ids.length) + ids.length) % ids.length;
  const messageId = ids[i];
  const payload = getMessagePayloadById(messageId, config?.defaultCtaText, "carousel");
  if (!payload) return null;
  return { ...payload, matchedRuleId: `${messageId}-${i}` };
}
